import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Badge,
  Button,
  DatePicker,
  DeleteIcon,
  Divider,
  Input,
  Modal,
  Radio,
  RadioGroup,
  RefreshIcon,
  Select,
  Text,
  TextButton,
  Textarea,
  type TypeSelectOption,
  type TypeTableRef,
  dialogService,
  toastService,
} from '@thefarmersfront/kitchen';
import { isDeepEqual } from '@thefarmersfront/kitchen-utensil';

import { useShallow } from 'zustand/react/shallow';

import { Bullet, FormField, InfoText, ModalContentsContainer, Table } from '~/components/common';

import {
  useGetFilterAllQuery,
  useGetRequestsRequestedQuery,
  useOperationDateTime,
  usePickupAddressHandler,
  usePutReceivingRequestMutation,
  useReceiveRequestDetailTableRowValidation,
} from '~/hooks/receive';
import { DEFAULT_PICKUP_ADDRESS_DATA, useLoadingStore, useReceiveStore, useUserInfoStore } from '~/store';
import {
  addIf,
  flattenDetailErrorState as flattenErrorList,
  focusFirstErrorField,
  formatDate,
  formatNumber,
  getDateByOffset,
  getReceiveRegisterDetailModalColumnDefs,
  renderFilterErrorMessage,
} from '~/utils';

import {
  DATE_FORMAT,
  DEFAULT_INPUT_STATUS,
  ERROR_REQUIRED_INPUT_STATUS,
  ERROR_REQUIRED_SELECT_STATUS,
  ERROR_VEHICLE_SIZE_REQUIRED_STATUS,
  MAX_RECEIVE_REQUEST_PRODUCT_COUNT,
  MAX_RECEIVE_REQUEST_SKU_COUNT,
  PICKUP_SHIPPING_TYPES,
  RECEIVE,
  RECEIVE_MODIFICATION_TYPE,
  RECEIVE_REQUEST_STATUS,
  RECEIVE_REQUEST_STATUS_BADGE_COLOR,
  RECEIVE_TOOLTIP_MESSAGE,
  RECEIVE_X_DOCK_CLUSTER_CODE,
  TOAST_MESSAGE,
  type TypeReceiveModificationType,
} from '~/constants';

import { renderOperationTimesDropdown, renderOperationTimesSelect } from '../ReceiveCustomRenderFunctions';
import { ReceivePickupInfoForm } from '../ReceivePickupInfoForm';
import { ReceiveRequestCaution } from '../ReceiveRequestCaution';

import type { TypeErrorResponseData, TypeInputState } from '~/types/common.types';
import type {
  TypeInitialPickupData,
  TypePickupAddressDataState,
  TypePickupAddressFormData,
  TypePickupData,
  TypePickupReleaseInfo,
  TypePickupReleaseInfoWithId,
  TypeReceiveDetailFilterState,
  TypeReceiveDetailOrderItem,
  TypeReceiveRequestDetailModalFilter,
} from '~/types/receive.types';

import styles from './ReceiveRequestDetailModal.module.scss';

/** 입고 상품 표 최대 높이. 넘치면 표 안에서 스크롤한다. */
const GOODS_TABLE_MAX_HEIGHT = 506;
const RELEASE_CODE_PARCEL = 'PARCEL';

const DEFAULT_REQUEST_DETAIL_FILTER: TypeReceiveRequestDetailModalFilter = {
  releaseCode: null,
  courier: '',
  invoiceNumber: '',
  expectedDate: null,
  expectedTime: null,
  center: null,
  temperature: null,
  xDock: 'false',
  xDockCenter: null,
  supplierCode: null,
  note: '',
  kurlyPickupAddress: 'default',
  milkrunPickupAddresses: {
    default: false,
    etc1: false,
    etc2: false,
  },
  pickupAddressData: {},
};

const DEFAULT_REQUEST_DETAIL_FILTER_STATE: TypeReceiveDetailFilterState = {
  releaseCode: DEFAULT_INPUT_STATUS,
  expectedDate: DEFAULT_INPUT_STATUS,
  expectedTime: DEFAULT_INPUT_STATUS,
  kurlyPickupAddress: DEFAULT_INPUT_STATUS,
  milkrunPickupAddresses: DEFAULT_INPUT_STATUS,
  pickupAddressData: {
    kurlyDefault: DEFAULT_PICKUP_ADDRESS_DATA,
    kurlyEtc: DEFAULT_PICKUP_ADDRESS_DATA,
    milkrunDefault: DEFAULT_PICKUP_ADDRESS_DATA,
    milkrunEtc1: DEFAULT_PICKUP_ADDRESS_DATA,
    milkrunEtc2: DEFAULT_PICKUP_ADDRESS_DATA,
  },
};

type TypeReceiveRequestDetailModalProps = {
  requestId: string;
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * [입고관리 > 입고요청관리] 입고요청상세 모달 컴포넌트
 */
const ReceiveRequestDetailModal = ({ requestId, open, onCancel, onConfirm }: TypeReceiveRequestDetailModalProps) => {
  const { setIsLoading } = useLoadingStore();

  const ownerCode = useUserInfoStore((state) => state.userInfo.ownerCode);

  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermissionNFA = hasPermission([{ ownerType: 'NFA' }]);

  const { data: filterOptions, isError: isFilterOptionsError } = useGetFilterAllQuery('ADD_RECEIVING_GOODS', {
    showGlobalErrorToast: false,
  });

  const requestDetailQuery = useGetRequestsRequestedQuery({
    ownerCode,
    requestId,
    enabled: open && !!ownerCode && !!requestId,
  });

  const [detailModalOrderGoodsList, setDetailModalOrderGoodsList] = useReceiveStore(
    useShallow((state) => [state.detailModalOrderGoodsList, state.setDetailModalOrderGoodsList])
  );

  useEffect(() => {
    if (requestDetailQuery.data) {
      setDetailModalOrderGoodsList(requestDetailQuery.data.orderItems);
    }
  }, [requestDetailQuery.data, setDetailModalOrderGoodsList]);

  useEffect(() => {
    if (isFilterOptionsError) {
      toastService.addToast({ type: 'error', persist: true, message: renderFilterErrorMessage() });
    }
  }, [isFilterOptionsError]);

  const totalSkuCount = new Set(detailModalOrderGoodsList.map(({ goodsCode }) => goodsCode)).size;
  const totalQtyCount = detailModalOrderGoodsList.reduce((acc, { totalQty }) => acc + totalQty, 0);

  const updateReceivingRequestMutation = usePutReceivingRequestMutation();
  const {
    errorList: rowErrorList,
    setErrorList: setRowErrorList,
    resetErrorList: resetRowErrorList,
    validateAllRows,
  } = useReceiveRequestDetailTableRowValidation();

  const [initialReceiveDetailFilter, setInitialReceiveDetailFilter] =
    useState<TypeReceiveRequestDetailModalFilter | null>(null);
  const [receiveDetailFilter, setReceiveDetailFilter] =
    useState<TypeReceiveRequestDetailModalFilter>(DEFAULT_REQUEST_DETAIL_FILTER);
  const [receiveDetailFilterState, setReceiveDetailFilterState] = useState<TypeReceiveDetailFilterState>(
    DEFAULT_REQUEST_DETAIL_FILTER_STATE
  );
  // 초기 픽업 출고지 데이터
  const [initialPickupInfos, setInitialPickupInfos] = useState<TypeInitialPickupData>({});

  const [selectedGoodsRow, setSelectedGoodsRow] = useState<TypeReceiveDetailOrderItem[]>([]);
  const [deletedGoodsRow, setDeletedGoodsRow] = useState<TypeReceiveDetailOrderItem[]>([]);

  const isReleaseCodeParcel = receiveDetailFilter.releaseCode?.value === RELEASE_CODE_PARCEL;
  const isReleaseCodeKurlyPickup = receiveDetailFilter.releaseCode?.value === PICKUP_SHIPPING_TYPES.KURLY_PICKUP;
  const isReleaseCodeMilkrunPickup = receiveDetailFilter.releaseCode?.value === PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP;

  const tableRef = useRef<TypeTableRef>(null);

  /** 에러 필드 DOM 참조 */
  const errorFieldRefs = useRef<Record<string, HTMLElement | null>>({});

  const isAfterCutoffTime = (expectedDate: Date | null): boolean => {
    if (!expectedDate) return false;

    const cutoffDateTime = getDateByOffset(-1, 'day', expectedDate);
    const cutoffTime = new Date(cutoffDateTime);
    cutoffTime.setHours(17, 0, 0, 0);

    const now = new Date();
    return now >= cutoffTime;
  };

  /**
   * 현재 시간이 입고예정일 D-1 17시 이후인지 여부
   * @note '요청완료' 상태이면서, 입고방법이 픽업인 경우에만 적용
   */
  const isReadOnlyPickupAddress = useMemo(
    () =>
      (isReleaseCodeKurlyPickup || isReleaseCodeMilkrunPickup) && isAfterCutoffTime(receiveDetailFilter.expectedDate),
    [isReleaseCodeKurlyPickup, isReleaseCodeMilkrunPickup, receiveDetailFilter.expectedDate]
  );

  const isReadOnlyStatus =
    hasPermissionNFA ||
    requestDetailQuery.data?.status === 'CONFIRMED' ||
    requestDetailQuery.data?.status === 'CANCELED' ||
    (requestDetailQuery.data?.status === 'REQUESTED' && isReadOnlyPickupAddress);

  const goodsListRef = useRef(detailModalOrderGoodsList);
  goodsListRef.current = detailModalOrderGoodsList;
  const getGoodsList = useCallback(() => goodsListRef.current, []);
  const { columnDefs } = useMemo(
    () =>
      getReceiveRegisterDetailModalColumnDefs({
        isReadOnly: isReadOnlyStatus,
        hasPermissionNFA,
        getTableData: getGoodsList,
        setTableData: setDetailModalOrderGoodsList,
      }),
    [isReadOnlyStatus, hasPermissionNFA, getGoodsList, setDetailModalOrderGoodsList]
  );

  // 입고예정일 관리 훅 사용
  const {
    operationDates,
    operationTimes: optionOperationTime,
    lastFetchedAt: lastFetchedOperationTimesTimestamp,
    handleDateChange: handleOperationDateChange,
    handleTimeChange: handleOperationTimeChange,
    refresh: handleRefreshOperationTimes,
  } = useOperationDateTime({
    orderId: requestDetailQuery.data?.orderId ?? '',
    releaseCode: receiveDetailFilter.releaseCode,
    expectedDate: receiveDetailFilter.expectedDate,
    expectedTime: receiveDetailFilter.expectedTime,
    isParcel: isReleaseCodeParcel,
    onUpdate: (updates) => {
      const updatedFilter: Partial<TypeReceiveRequestDetailModalFilter> = {};

      if (updates.expectedDate !== undefined) {
        updatedFilter.expectedDate = updates.expectedDate;
      }

      if (updates.expectedTime !== undefined) {
        if (typeof updates.expectedTime === 'object' && updates.expectedTime !== null) {
          updatedFilter.expectedTime = updates.expectedTime.value;
        } else {
          updatedFilter.expectedTime = updates.expectedTime;
        }
      }

      setReceiveDetailFilter((prev) => ({ ...prev, ...updatedFilter }));
    },
    onValidationChange: (state) => {
      setReceiveDetailFilterState((prev) => ({ ...prev, ...state }));
    },
    isDetailMode: true,
    initialOperationTimeId: requestDetailQuery.data?.dailyCapaId,
  });

  useEffect(() => {
    if (requestDetailQuery.data && filterOptions) {
      fetchFilter();
    }
  }, [requestDetailQuery.data, filterOptions]);

  useEffect(() => {
    return () => resetRowErrorList();
  }, []);

  // 픽업 출고지 관리 훅 사용
  const { handlePickupAddressDataChange, requestDefaultPickupAddress } = usePickupAddressHandler({
    supplierCode: requestDetailQuery.data?.supplierCode ?? '',
    currentData: receiveDetailFilter,
    onDataChange: setReceiveDetailFilter,
  });

  if (!requestDetailQuery.data) {
    return <div></div>;
  }

  const createPickupData = (value: TypePickupReleaseInfo) => ({
    zipCode: value.zipNo,
    address: value.address,
    detailAddress: value.detailAddress ?? '',
    contactNumber: value.phoneNo,
    isUseVehicleSize: Boolean(value.vehicleSizeCode),
    vehicleSize: value.vehicleSizeCode
      ? (filterOptions?.vehicleSize.find((option) => option.value === value.vehicleSizeCode) ?? null)
      : null,
    palletQuantity: value.palletQty,
    memo: value.memo ?? '',
  });

  /** 픽업 주소 데이터 초기화 */
  const initializePickupAddressData = (
    releaseCode: string,
    defaultInfos: TypePickupReleaseInfoWithId[] = [],
    extraInfos: TypePickupReleaseInfoWithId[] = []
  ) => {
    const pickupAddressData: TypePickupAddressFormData['pickupAddressData'] = {
      kurlyDefault: undefined,
      kurlyEtc: undefined,
      milkrunDefault: undefined,
      milkrunEtc1: undefined,
      milkrunEtc2: undefined,
    };

    let kurlyPickupAddress: 'default' | 'etc' = 'default';
    const milkrunPickupAddresses = {
      default: false,
      etc1: false,
      etc2: false,
    };

    // 입고방법에 따라 기본/기타 픽업 출고지 정보 초기화
    if (releaseCode === PICKUP_SHIPPING_TYPES.KURLY_PICKUP) {
      // 컬리픽업인 경우
      if (defaultInfos.length > 0) {
        pickupAddressData.kurlyDefault = createPickupData(defaultInfos[0]);
      }
      if (extraInfos.length > 0) {
        pickupAddressData.kurlyEtc = createPickupData(extraInfos[0]);
        kurlyPickupAddress = 'etc';
      }
    } else if (releaseCode === PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP) {
      // 밀크런픽업인 경우
      if (defaultInfos.length > 0) {
        pickupAddressData.milkrunDefault = createPickupData(defaultInfos[0]);
        milkrunPickupAddresses.default = true;
      }
      if (extraInfos.length >= 1) {
        pickupAddressData.milkrunEtc1 = createPickupData(extraInfos[0]);
        milkrunPickupAddresses.etc1 = true;

        if (extraInfos.length === 2) {
          pickupAddressData.milkrunEtc2 = createPickupData(extraInfos[1]);
          milkrunPickupAddresses.etc2 = true;
        }
      }
    }

    return { pickupAddressData, kurlyPickupAddress, milkrunPickupAddresses };
  };

  const fetchFilter = async () => {
    try {
      setIsLoading({ dimmed: false });

      // form 입력값 초기화
      const {
        cluster,
        warehouse,
        xdock,
        xdockCenter,
        releaseCode,
        courierName,
        invoiceNo,
        receivingExpectedDate,
        dailyCapaId,
        supplierCode,
        remarks,
        defaultPickupReleaseInfos,
        extraPickupReleaseInfos,
      } = requestDetailQuery.data;

      const { pickupAddressData, kurlyPickupAddress, milkrunPickupAddresses } = initializePickupAddressData(
        releaseCode,
        defaultPickupReleaseInfos,
        extraPickupReleaseInfos
      );

      const initializedFilter: TypeReceiveRequestDetailModalFilter = {
        ...DEFAULT_REQUEST_DETAIL_FILTER,
        releaseCode: filterOptions?.releaseCode.find(({ value }) => value === releaseCode) ?? null,
        courier: courierName ?? '',
        invoiceNumber: invoiceNo ?? '',
        expectedDate: receivingExpectedDate ? new Date(receivingExpectedDate) : null,
        expectedTime: String(dailyCapaId),
        center: filterOptions?.center.find(({ value }) => value === cluster) ?? null,
        temperature:
          filterOptions?.groupedTemperatureByCenter[cluster]?.find(({ value }) => value === warehouse) ?? null,
        xDock: xdock ? 'true' : 'false',
        xDockCenter:
          filterOptions?.groupedTemperatureByCenter[RECEIVE_X_DOCK_CLUSTER_CODE]?.find(
            ({ value }) => value === xdockCenter
          ) ?? null,
        supplierCode: filterOptions?.supplier.find(({ value }) => value === supplierCode) ?? null,
        note: remarks ?? '',
        kurlyPickupAddress,
        milkrunPickupAddresses,
        pickupAddressData,
      };

      // 초기 픽업 출고지 정보 저장
      const initialPickupData: TypeInitialPickupData = {
        ...addIf(releaseCode === PICKUP_SHIPPING_TYPES.KURLY_PICKUP, {
          ...addIf(defaultPickupReleaseInfos?.[0], 'kurlyDefault'),
          ...addIf(extraPickupReleaseInfos?.[0], 'kurlyEtc'),
        }),
        ...addIf(releaseCode === PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP, {
          ...addIf(defaultPickupReleaseInfos?.[0], 'milkrunDefault'),
          ...addIf(extraPickupReleaseInfos?.[0], 'milkrunEtc1'),
          ...addIf(extraPickupReleaseInfos?.[1], 'milkrunEtc2'),
        }),
      };

      setInitialPickupInfos(initialPickupData);

      // 입고방법이 픽업인 경우, 기본 주소 정보를 API에서 가져와서 업데이트
      if (releaseCode === PICKUP_SHIPPING_TYPES.KURLY_PICKUP || releaseCode === PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP) {
        requestDefaultPickupAddress(releaseCode, initializedFilter, (initializedFilterWithDefaultAddress) => {
          setReceiveDetailFilter(initializedFilterWithDefaultAddress);
          setInitialReceiveDetailFilter(initializedFilterWithDefaultAddress);
        });
      } else {
        setReceiveDetailFilter(initializedFilter);
        setInitialReceiveDetailFilter(initializedFilter);
      }
    } catch (error) {
      toastService.addToast({
        type: 'error',
        persist: true,
        message: (error as TypeErrorResponseData['error'])?.message || renderFilterErrorMessage(),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUpdateRequest = () => {
    updateReceivingRequestMutation.mutate(
      {
        ownerCode,
        orderId: requestDetailQuery.data.orderId,
        filterData: receiveDetailFilter,
        initialPickupData: initialPickupInfos,
        tableData: detailModalOrderGoodsList,
        deletedTableData: deletedGoodsRow,
      },
      {
        onSuccess: () => {
          toastService.addToast({
            message: '수정되었습니다.',
          });
          onConfirm();
        },
        onError: async (error) => {
          if (
            error?.code === RECEIVE.RECEIVE_DAILY_CAPA_NOT_FOUND ||
            error?.code === RECEIVE.PICKUP_EDIT_TIME_EXCEEDED
          ) {
            const updatedFilter = {
              ...receiveDetailFilter,
              expectedDate: null,
              expectedTime: null,
            };

            setReceiveDetailFilter(updatedFilter);

            // refresh가 내부에서 expectedDate/expectedTime validation을 갱신하므로 먼저 await 후 functional update로 합친다
            await handleRefreshOperationTimes();

            const expectedDateError: TypeInputState = { status: 'error', message: '요청 불가능한 날짜입니다.' };
            setReceiveDetailFilterState((prev) => ({ ...prev, expectedDate: expectedDateError }));

            const errorStateMap = flattenErrorList({ ...receiveDetailFilterState, expectedDate: expectedDateError });
            focusFirstErrorField(errorFieldRefs.current, errorStateMap);

            return;
          }

          toastService.addToast({
            type: 'error',
            message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR,
          });
        },
      }
    );
  };

  const handleClickGoodsDelete = () => {
    // 삭제된(선택된) 값을 제외해 업데이트, 삭제된 값 따로 보관
    // 선택은 행 id(index)로 유지돼, 선택한 행을 편집하면 선택 목록에는 편집 전 행 객체가 남는다. 그래서 index 로 찾는다.
    const selectedIndexes = new Set(selectedGoodsRow.map(({ index }) => index));
    const selectedRowIndexes = new Set(
      detailModalOrderGoodsList.flatMap((goods, index) => (selectedIndexes.has(goods.index) ? [index] : []))
    );
    const updatedOrderItems = detailModalOrderGoodsList.filter((_, index) => !selectedRowIndexes.has(index));
    const deletedOrderItems = detailModalOrderGoodsList
      .filter((_, index) => selectedRowIndexes.has(index))
      .map((item) => ({
        ...item,
        modificationType: RECEIVE_MODIFICATION_TYPE.DELETE as TypeReceiveModificationType,
      }));

    setDetailModalOrderGoodsList(updatedOrderItems);
    setDeletedGoodsRow(deletedOrderItems);

    setRowErrorList(rowErrorList.filter((_, index) => !selectedRowIndexes.has(index)));
  };

  const handleChangeRowSelection = (rows: TypeReceiveDetailOrderItem[]) => {
    setSelectedGoodsRow(rows);
  };

  const handleChangeReleaseCode = (
    _: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    option: TypeSelectOption<string>
  ) => {
    if (option === receiveDetailFilter.releaseCode) return;

    const isParcelSelected = option.value === RELEASE_CODE_PARCEL;

    const updatedFilter = {
      ...receiveDetailFilter,
      releaseCode: option,
      ...(!isParcelSelected && { courier: '', invoiceNumber: '' }),
      expectedDate: null,
      expectedTime: null,
      // 픽업 타입 변경 시 기본 주소 선택
      ...(option.value === PICKUP_SHIPPING_TYPES.KURLY_PICKUP && {
        kurlyPickupAddress: 'default' as const,
      }),
      ...(option.value === PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP && {
        milkrunPickupAddresses: {
          default: true,
          etc1: false,
          etc2: false,
        },
      }),
    };

    if (option.value === PICKUP_SHIPPING_TYPES.KURLY_PICKUP || option.value === PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP) {
      // 픽업 주소 데이터를 가져온 후 상태 업데이트
      requestDefaultPickupAddress(option.value, updatedFilter);
    } else {
      setReceiveDetailFilter(updatedFilter);
    }

    setReceiveDetailFilterState({
      ...receiveDetailFilterState,
      releaseCode: DEFAULT_INPUT_STATUS,
      expectedDate: DEFAULT_INPUT_STATUS,
      expectedTime: DEFAULT_INPUT_STATUS,
    });
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    setReceiveDetailFilter((prev) => ({
      ...prev,
      [e.target.id]: e.target.value,
    }));
  };

  const handleCancel = () => {
    if (isReadOnlyStatus) {
      onCancel();
    } else {
      // isDeepEqual은 Date를 비교 대상으로 받지 않아(throw) 날짜를 문자열로 바꿔 비교한다
      const normalizeForComparison = (filter: TypeReceiveRequestDetailModalFilter | null) =>
        filter && { ...filter, expectedDate: formatDate(filter.expectedDate, DATE_FORMAT.DATE) ?? null };

      const isUpdated = !(
        isDeepEqual(normalizeForComparison(initialReceiveDetailFilter), normalizeForComparison(receiveDetailFilter)) &&
        isDeepEqual(requestDetailQuery.data?.orderItems, detailModalOrderGoodsList)
      );

      if (isUpdated) {
        dialogService.addDialog({
          type: 'confirm',
          children: '수정된 내용이 저장되지 않습니다.\n페이지를 나가시겠습니까?',
          confirmText: '나가기',
          onConfirm: onCancel,
        });
      } else {
        onCancel();
      }
    }
  };

  /** 픽업 출고지 데이터 유효성 검사 */
  const validatePickupAddressData = (pickupData?: TypePickupData): TypePickupAddressDataState => {
    if (!pickupData) {
      return {
        address: ERROR_REQUIRED_INPUT_STATUS,
        palletQuantity: ERROR_REQUIRED_INPUT_STATUS,
        vehicleSize: DEFAULT_INPUT_STATUS,
      };
    }

    const isValidAddress = Boolean(pickupData.address);
    const isValidPalletQuantity = Boolean(pickupData.palletQuantity);
    const isValidVehicleSize = !pickupData.isUseVehicleSize || Boolean(pickupData.vehicleSize);

    return {
      address: isValidAddress ? DEFAULT_INPUT_STATUS : ERROR_REQUIRED_INPUT_STATUS,
      palletQuantity: isValidPalletQuantity ? DEFAULT_INPUT_STATUS : ERROR_REQUIRED_INPUT_STATUS,
      vehicleSize: isValidVehicleSize ? DEFAULT_INPUT_STATUS : ERROR_VEHICLE_SIZE_REQUIRED_STATUS,
    };
  };

  const checkRequiredFilter = (): TypeReceiveDetailFilterState => {
    const isValidExpectedDate = !receiveDetailFilter.expectedDate;
    const isValidExpectedTime = !isReleaseCodeParcel && !receiveDetailFilter.expectedTime;

    // 컬리픽업 검증
    let isValidKurlyPickupAddressSelection = true;
    const kurlyPickupAddressDataState: {
      kurlyDefault?: TypePickupAddressDataState;
      kurlyEtc?: TypePickupAddressDataState;
    } = {};

    if (isReleaseCodeKurlyPickup) {
      // 선택 여부 확인
      isValidKurlyPickupAddressSelection = Boolean(receiveDetailFilter.kurlyPickupAddress);

      // 필수값 미입력 검증
      if (receiveDetailFilter.kurlyPickupAddress === 'default') {
        const validationResult = validatePickupAddressData(receiveDetailFilter.pickupAddressData?.kurlyDefault);
        kurlyPickupAddressDataState.kurlyDefault = validationResult;
      } else if (receiveDetailFilter.kurlyPickupAddress === 'etc') {
        const validationResult = validatePickupAddressData(receiveDetailFilter.pickupAddressData?.kurlyEtc);
        kurlyPickupAddressDataState.kurlyEtc = validationResult;
      }
    }

    // 밀크런픽업 검증
    let isValidMilkrunPickupAddressesSelection = true;
    const milkrunPickupAddressData: {
      milkrunDefault?: TypePickupAddressDataState;
      milkrunEtc1?: TypePickupAddressDataState;
      milkrunEtc2?: TypePickupAddressDataState;
    } = {};

    if (isReleaseCodeMilkrunPickup) {
      // 선택 여부 확인
      const hasAnySelection =
        receiveDetailFilter.milkrunPickupAddresses.default ||
        receiveDetailFilter.milkrunPickupAddresses.etc1 ||
        receiveDetailFilter.milkrunPickupAddresses.etc2;

      isValidMilkrunPickupAddressesSelection = hasAnySelection;

      // 필수값 미입력 검증
      if (receiveDetailFilter.milkrunPickupAddresses.default) {
        const validationResult = validatePickupAddressData(receiveDetailFilter.pickupAddressData?.milkrunDefault);
        milkrunPickupAddressData.milkrunDefault = validationResult;
      }
      if (receiveDetailFilter.milkrunPickupAddresses.etc1) {
        const validationResult = validatePickupAddressData(receiveDetailFilter.pickupAddressData?.milkrunEtc1);
        milkrunPickupAddressData.milkrunEtc1 = validationResult;
      }
      if (receiveDetailFilter.milkrunPickupAddresses.etc2) {
        const validationResult = validatePickupAddressData(receiveDetailFilter.pickupAddressData?.milkrunEtc2);
        milkrunPickupAddressData.milkrunEtc2 = validationResult;
      }
    }

    return {
      releaseCode: receiveDetailFilter.releaseCode ? DEFAULT_INPUT_STATUS : ERROR_REQUIRED_SELECT_STATUS,
      expectedDate: isValidExpectedDate ? ERROR_REQUIRED_SELECT_STATUS : DEFAULT_INPUT_STATUS,
      expectedTime: isValidExpectedTime ? ERROR_REQUIRED_SELECT_STATUS : DEFAULT_INPUT_STATUS,
      kurlyPickupAddress: isValidKurlyPickupAddressSelection ? DEFAULT_INPUT_STATUS : ERROR_REQUIRED_SELECT_STATUS,
      milkrunPickupAddresses: isValidMilkrunPickupAddressesSelection
        ? DEFAULT_INPUT_STATUS
        : ERROR_REQUIRED_SELECT_STATUS,
      pickupAddressData: {
        kurlyDefault: kurlyPickupAddressDataState.kurlyDefault || DEFAULT_PICKUP_ADDRESS_DATA,
        kurlyEtc: kurlyPickupAddressDataState.kurlyEtc || DEFAULT_PICKUP_ADDRESS_DATA,
        milkrunDefault: milkrunPickupAddressData.milkrunDefault || DEFAULT_PICKUP_ADDRESS_DATA,
        milkrunEtc1: milkrunPickupAddressData.milkrunEtc1 || DEFAULT_PICKUP_ADDRESS_DATA,
        milkrunEtc2: milkrunPickupAddressData.milkrunEtc2 || DEFAULT_PICKUP_ADDRESS_DATA,
      },
    };
  };

  const hasPickupAddressDataError = (
    pickupAddressDataState: TypeReceiveDetailFilterState['pickupAddressData']
  ): boolean => {
    return Object.values(pickupAddressDataState).some(hasAddressError);
  };

  const hasAddressError = (addressDataState: TypePickupAddressDataState): boolean => {
    return Object.values(addressDataState).some((fieldState) => fieldState.status === 'error');
  };

  const validateModal = () => {
    // 필터 입력값 유효성 검사
    const updatedFilterState = checkRequiredFilter();

    setReceiveDetailFilterState(updatedFilterState);

    const hasFilterError =
      updatedFilterState.releaseCode.status === 'error' ||
      updatedFilterState.expectedDate.status === 'error' ||
      updatedFilterState.expectedTime.status === 'error' ||
      updatedFilterState.kurlyPickupAddress.status === 'error' ||
      updatedFilterState.milkrunPickupAddresses.status === 'error' ||
      hasPickupAddressDataError(updatedFilterState.pickupAddressData);

    // 테이블 입력값 유효성 검사
    if (detailModalOrderGoodsList.length === 0) {
      dialogService.addDialog({
        children: '입고할 상품을 1개 이상 추가해주세요.',
      });
      return;
    }

    const { errorList, firstErrorRowIndex: errorRowIndex } = validateAllRows();

    const hasTableError = errorList.some((error) => Object.values(error).includes('error'));

    if (hasTableError) {
      //전체에서 에러가 2개 이상인 경우 스크롤
      const totalErrorCount = errorList.reduce((acc, error) => {
        const rowErrorCount = Object.values(error).filter((value) => value === 'error').length;
        return acc + rowErrorCount;
      }, 0);
      const hasMoreThanTwoErrorFields = totalErrorCount >= 2;

      if (hasMoreThanTwoErrorFields && errorRowIndex !== null) {
        tableRef.current?.scrollToIndex(errorRowIndex, { align: 'center' });
      }

      dialogService.addDialog({
        children: `입력 조건에 맞지 않는 상품 정보가 있습니다.\n다시 확인 후 시도해주세요.`,
      });

      return;
    }

    if (!hasFilterError) {
      dialogService.addDialog({
        children: '요청을 수정하시겠습니까?',
        type: 'confirm',
        confirmText: '저장',
        onConfirm: fetchUpdateRequest,
      });
    } else {
      const errorStateMap = flattenErrorList(updatedFilterState);
      focusFirstErrorField(errorFieldRefs.current, errorStateMap);
    }
  };

  const handleConfirm = () => {
    if (isReadOnlyStatus) {
      onCancel();
      return;
    }

    validateModal();
  };

  return (
    <Modal
      title={
        <div className={styles['modal-title']}>
          <Text size="xxlarge" weight="semi-bold">
            입고 요청 상세
          </Text>
          <Badge variant="light" status color={RECEIVE_REQUEST_STATUS_BADGE_COLOR[requestDetailQuery.data.status]}>
            {RECEIVE_REQUEST_STATUS[requestDetailQuery.data.status]}
          </Badge>
        </div>
      }
      size="xlarge"
      confirmText={isReadOnlyStatus ? '확인' : '저장'}
      open={open}
      onCancel={isReadOnlyStatus ? undefined : handleCancel}
      onConfirm={handleConfirm}
    >
      <ModalContentsContainer>
        {requestDetailQuery.data.status === 'REQUESTED' && <ReceiveRequestCaution />}
        {requestDetailQuery.data.status === 'CANCELED' && (
          <div className={styles['cancel-info-section']}>
            <div className={styles['cancel-info-row']}>
              <Text className={styles['cancel-info-label']} size="medium" weight="semi-bold" color="error">
                취소자
              </Text>
              <Text size="medium">{requestDetailQuery.data.cancelUserId ?? ''}</Text>
            </div>
            <div className={styles['cancel-info-row']}>
              <Text className={styles['cancel-info-label']} size="medium" weight="semi-bold" color="error">
                취소사유
              </Text>
              <Text size="medium">{requestDetailQuery.data.cancelReason ?? ''}</Text>
            </div>
          </div>
        )}
        <div className={styles['input-section']}>
          <Text size="xxlarge" weight="bold">
            입고 예정 정보
          </Text>
          <FormField
            label="입고지"
            tooltip={{
              description: RECEIVE_TOOLTIP_MESSAGE.INBOUND_AREA_ADDRESS,
            }}
            contents={<Text size="large">{requestDetailQuery.data.inboundAreaAddress ?? ''}</Text>}
            contentsClassName={styles['content-text']}
          />
          <Divider color="gray100" />
          <FormField
            ref={(el) => {
              if (el) errorFieldRefs.current.releaseCode = el;
            }}
            label="입고 방법"
            required
            tooltip={{
              description: RECEIVE_TOOLTIP_MESSAGE.RELEASE_CODE_PICKUP_EXPECTED_DATE,
              maxWidth: '226px',
            }}
            contents={
              <Select
                id="releaseCode"
                maxWidth="229px"
                size="small"
                placeholder="선택"
                disabled={isReadOnlyStatus}
                options={filterOptions?.releaseCode ?? []}
                value={receiveDetailFilter.releaseCode}
                onChange={handleChangeReleaseCode}
              />
            }
          />
          {(isReleaseCodeKurlyPickup || isReleaseCodeMilkrunPickup) && (
            <ReceivePickupInfoForm
              disabled={isReadOnlyStatus}
              pickupType={isReleaseCodeKurlyPickup ? 'kurly' : 'milkrun'}
              data={{
                kurlyPickupAddress: receiveDetailFilter.kurlyPickupAddress,
                milkrunPickupAddresses: receiveDetailFilter.milkrunPickupAddresses,
                pickupAddressData: receiveDetailFilter.pickupAddressData ?? {},
              }}
              dataState={{
                kurlyPickupAddress: receiveDetailFilterState.kurlyPickupAddress,
                milkrunPickupAddresses: receiveDetailFilterState.milkrunPickupAddresses,
                pickupAddressData: receiveDetailFilterState.pickupAddressData,
              }}
              onKurlyPickupAddressChange={(value) => {
                setReceiveDetailFilter({
                  ...receiveDetailFilter,
                  kurlyPickupAddress: value,
                });
                setReceiveDetailFilterState({ ...receiveDetailFilterState, kurlyPickupAddress: DEFAULT_INPUT_STATUS });
              }}
              onMilkrunPickupAddressChange={(addressType, checked) => {
                setReceiveDetailFilter({
                  ...receiveDetailFilter,
                  milkrunPickupAddresses: {
                    ...receiveDetailFilter.milkrunPickupAddresses,
                    [addressType]: checked,
                  },
                });
                setReceiveDetailFilterState({
                  ...receiveDetailFilterState,
                  milkrunPickupAddresses: DEFAULT_INPUT_STATUS,
                });
              }}
              onPickupAddressDataChange={handlePickupAddressDataChange}
              onResetPickupAddressDataState={(addressType, key) => {
                setReceiveDetailFilterState({
                  ...receiveDetailFilterState,
                  pickupAddressData: {
                    ...receiveDetailFilterState.pickupAddressData,
                    [addressType]: {
                      ...receiveDetailFilterState.pickupAddressData?.[addressType],
                      [key]: DEFAULT_INPUT_STATUS,
                    },
                  },
                });
              }}
              errorFieldRefs={errorFieldRefs}
            />
          )}
          {isReleaseCodeParcel && (
            <>
              <FormField
                label="택배사"
                contents={
                  <Input
                    id="courier"
                    type="text"
                    size="small"
                    placeholder="입력"
                    count
                    disabled={isReadOnlyStatus}
                    maxLength={15}
                    maxWidth="300px"
                    value={receiveDetailFilter.courier}
                    onChange={handleChangeInput}
                  />
                }
              />
              <FormField
                label="운송장번호"
                contents={
                  <Input
                    id="invoiceNumber"
                    type="text"
                    size="small"
                    placeholder="최대 1개 입력"
                    count
                    disabled={isReadOnlyStatus}
                    maxLength={20}
                    maxWidth="300px"
                    value={receiveDetailFilter.invoiceNumber}
                    onChange={handleChangeInput}
                  />
                }
              />
            </>
          )}
          <Divider color="gray100" />
          <div>
            <FormField
              ref={(el) => {
                if (el) errorFieldRefs.current.expectedDateTime = el;
              }}
              label="입고예정일시"
              required
              tooltip={{
                description: RECEIVE_TOOLTIP_MESSAGE.RECEIVE_EXPECTED_DATE,
              }}
              contents={
                <>
                  <div className={styles['date-time-inputs']}>
                    <DatePicker
                      size="small"
                      placeholder="날짜 선택"
                      maxWidth="229px"
                      portalTarget={document.body}
                      shouldCloseOnNotInView
                      disabled={isReadOnlyStatus || !receiveDetailFilter.releaseCode}
                      status={receiveDetailFilterState.expectedDate.status}
                      validationMessage={receiveDetailFilterState.expectedDate.message}
                      value={receiveDetailFilter.expectedDate}
                      isDateDisabled={(date) => {
                        const formattedDate = formatDate(date, 'YYYY-MM-DD');
                        return formattedDate ? !operationDates.includes(formattedDate) : false;
                      }}
                      onChange={handleOperationDateChange}
                      onFocus={() =>
                        setReceiveDetailFilterState({ ...receiveDetailFilterState, expectedDate: DEFAULT_INPUT_STATUS })
                      }
                    />
                    <Select
                      size="small"
                      placeholder="시간 선택"
                      maxWidth="317px"
                      dropdownPortalTarget={document.body}
                      shouldCloseOnNotInView
                      labelRenderer={(value) => {
                        const isDisabled = !receiveDetailFilter.expectedDate || isReleaseCodeParcel;
                        return renderOperationTimesSelect(value, isDisabled);
                      }}
                      dropdownRenderer={(option) => {
                        const isSelected = option.value === receiveDetailFilter.expectedTime;
                        const isDisabled = !isSelected && option.disabled === true;
                        return renderOperationTimesDropdown(option, isDisabled, isSelected);
                      }}
                      disabled={isReadOnlyStatus || !receiveDetailFilter.expectedDate || isReleaseCodeParcel}
                      status={receiveDetailFilterState.expectedTime.status}
                      validationMessage={receiveDetailFilterState.expectedTime.message}
                      value={optionOperationTime.find(({ value }) => value === receiveDetailFilter.expectedTime)}
                      options={optionOperationTime}
                      onChange={(
                        _: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
                        value: TypeSelectOption<string>
                      ) => {
                        handleOperationTimeChange(value);
                      }}
                      onClick={() =>
                        setReceiveDetailFilterState({ ...receiveDetailFilterState, expectedTime: DEFAULT_INPUT_STATUS })
                      }
                    />
                    {!isReadOnlyStatus && lastFetchedOperationTimesTimestamp && (
                      <div className={styles['timestamp-wrapper']}>
                        <TextButton
                          size="tiny"
                          weight="regular"
                          color="tertiary"
                          leftIcon={<RefreshIcon size={16} color="gray600" />}
                          onClick={handleRefreshOperationTimes}
                        >
                          {formatDate(lastFetchedOperationTimesTimestamp, DATE_FORMAT.DATE_TIME)}
                        </TextButton>
                      </div>
                    )}
                  </div>
                  {isReleaseCodeParcel && (
                    <InfoText>택배사 발송일이 아닌, 물류센터에 실제 도착하는 날짜를 선택 바랍니다.</InfoText>
                  )}
                </>
              }
              contentsClassName={styles['content-expected-date']}
            />
          </div>
        </div>
        <div className={styles['input-section']}>
          <Text size="xxlarge" weight="bold">
            기본 정보
          </Text>
          <div>
            <FormField
              label="입고 센터"
              required
              contents={
                <>
                  <Select size="small" placeholder="선택" disabled options={[]} value={receiveDetailFilter.center} />
                  <Select
                    size="small"
                    placeholder="선택"
                    disabled
                    options={[]}
                    value={receiveDetailFilter.temperature}
                  />
                </>
              }
              contentsClassName={styles['content-inputs']}
            />
            <FormField
              label="입고대행서비스"
              required
              contents={
                <RadioGroup name="xdock" size="medium" disabled value={receiveDetailFilter.xDock}>
                  <Radio value="true" label="이용함" />
                  <Radio value="false" label="이용안함" />
                </RadioGroup>
              }
              contentsClassName={styles['content-radio']}
            />
            {receiveDetailFilter.xDock === 'true' && (
              <FormField
                label="경유센터"
                required
                contents={
                  <Select
                    size="small"
                    placeholder="선택"
                    maxWidth="229px"
                    disabled
                    options={[]}
                    value={receiveDetailFilter.xDockCenter}
                  />
                }
              />
            )}
          </div>
          <Divider color="gray100" />
          <div className={styles['text-area-section']}>
            <FormField
              label="공급사"
              required
              contents={
                <Select
                  size="small"
                  placeholder="선택"
                  maxWidth="229px"
                  disabled
                  options={filterOptions?.supplier ?? []}
                  value={receiveDetailFilter.supplierCode}
                />
              }
            />
            <FormField
              label="비고"
              contents={
                <Textarea
                  id="note"
                  size="small"
                  maxWidth="503px"
                  height="78px"
                  placeholder="요청 또는 특이사항 입력"
                  count
                  disabled={isReadOnlyStatus}
                  maxLength={255}
                  value={receiveDetailFilter.note}
                  onChange={handleChangeInput}
                />
              }
            />
          </div>
        </div>
        <div className={styles['view-section']}>
          <div className={styles['table-top-field']}>
            <div className={styles['table-top-left-field']}>
              <Text className={styles['title']} size="xxlarge" weight="bold">
                입고 상품
              </Text>
              <Text size="medium" weight="semi-bold" color="tertiary">
                입고 가능 품목 {formatNumber(totalSkuCount)}/{formatNumber(MAX_RECEIVE_REQUEST_SKU_COUNT)}
              </Text>
              <Bullet fill="#848F9A" />
              <Text size="medium" weight="semi-bold" color="tertiary">
                입고 가능 수량 {formatNumber(totalQtyCount)}/{formatNumber(MAX_RECEIVE_REQUEST_PRODUCT_COUNT)}
              </Text>
            </div>
            {!isReadOnlyStatus && (
              <div className={styles['table-top-right-field']}>
                <Button
                  size="small"
                  variant="outlined"
                  color="tertiary"
                  leftIcon={<DeleteIcon size={16} />}
                  disabled={selectedGoodsRow.length === 0}
                  onClick={handleClickGoodsDelete}
                >
                  삭제
                </Button>
              </div>
            )}
          </div>
          <div className={styles['table-wrapper']}>
            <Table
              tableRef={tableRef}
              className={styles['table']}
              autoHeight
              maxHeight={GOODS_TABLE_MAX_HEIGHT}
              data={detailModalOrderGoodsList}
              columns={columnDefs}
              getRowId={(row) => String(row.index)}
              keepScrollOnDataChange
              selection={{ onChange: handleChangeRowSelection }}
            />
          </div>
        </div>
      </ModalContentsContainer>
    </Modal>
  );
};

export default ReceiveRequestDetailModal;
